/**
 * Feb 2026 · Settings › Workspace › Modules
 * ------------------------------------------------------------------
 * Per-workspace kill-switches for optional modules. Currently exposes
 * the Brand module toggle — off ⇒ Brand column, dropdown, filter and
 * nav entries disappear across the entire product.
 *
 *   GET /workspaces/{ws}/settings/modules
 *   PUT /workspaces/{ws}/settings/modules  { brands_enabled }
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Save, Gem, ShieldOff } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";

export default function ModulesSettings() {
  const { activeWorkspaceId } = useAuth();
  const { hasPerm, refresh: refreshPerms } = useWorkspacePerms();
  const canManage = hasPerm("workspace.manage") || hasPerm("workspace.admin");
  const [brandsOn, setBrandsOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/settings/modules`)
      .then((r) => { if (!cancelled) setBrandsOn(r.data?.brands_enabled !== false); })
      .catch((e) => { if (!cancelled) toast.error(apiErrorMessage(e, "Failed to load module settings")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeWorkspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/settings/modules`, { brands_enabled: brandsOn });
      toast.success("Module settings saved");
      // Bust the perm cache so `modules.brands` re-evaluates and nav / columns update immediately.
      refreshPerms && refreshPerms();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="settings-modules">
      <PageHeader title="Workspace modules" subtitle="Toggle optional modules on or off for this workspace." icon={ShieldOff} />
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="border border-border">
          <ModuleRow
            icon={Gem}
            title="Brand module"
            description="Adds a Brand field, column, filter and dropdown throughout Projects, Clients, Content Calendar, Studio Booking and more. Turn off if your workspace doesn't work with sub-brands."
            value={brandsOn}
            onChange={setBrandsOn}
            disabled={!canManage}
            testid="mod-brands"
          />
        </div>
      )}
      <div className="flex justify-end mt-4">
        <Button className="rounded-none" onClick={save} disabled={!canManage || saving || loading} data-testid="mod-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </div>
      {!canManage && (
        <p className="text-[11px] text-muted-foreground italic mt-2">
          Only workspace admins / owners can change module settings.
        </p>
      )}
    </div>
  );
}

function ModuleRow({ icon: Icon, title, description, value, onChange, disabled, testid }) {
  return (
    <div className="flex items-start gap-3 p-4 border-b border-border last:border-b-0">
      <div className="w-10 h-10 border border-border flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-foreground/70" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold uppercase tracking-wide">{title}</div>
        <div className="text-xs text-muted-foreground mt-1 max-w-lg">{description}</div>
      </div>
      <button type="button" role="switch" aria-checked={value} disabled={disabled}
              onClick={() => onChange(!value)}
              data-testid={`${testid}-toggle`}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? "bg-primary" : "bg-muted-foreground/30"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${value ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </div>
  );
}
